import React, { useEffect, useMemo, useState } from 'react';
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LocateFixed, MapPin } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const FALLBACK_CENTER = [12.9716, 77.5946];

const parseCoordinate = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const MapController = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (!center) return;
    map.setView(center, Math.max(map.getZoom(), 15), { animate: true });
    setTimeout(() => map.invalidateSize(), 0);
  }, [center, map]);

  return null;
};

const MapInteractions = ({ onSelect }) => {
  useMapEvents({
    click(event) {
      onSelect([event.latlng.lat, event.latlng.lng]);
    }
  });

  return null;
};

const GeofenceMapPicker = ({
  enabled,
  latitude,
  longitude,
  radius,
  onLocationChange,
  onRadiusChange,
  title = 'Location on map',
  note = 'Click anywhere on the map or drag the pin to change the geofence center.'
}) => {
  const [mapCenter, setMapCenter] = useState(FALLBACK_CENTER);

  const selectedPosition = useMemo(() => {
    const lat = parseCoordinate(latitude);
    const lng = parseCoordinate(longitude);
    if (lat === null || lng === null) return null;
    return [lat, lng];
  }, [latitude, longitude]);

  useEffect(() => {
    if (!enabled) return;

    if (selectedPosition) {
      setMapCenter(selectedPosition);
      return;
    }

    if (!navigator.geolocation) {
      setMapCenter(FALLBACK_CENTER);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextCenter = [position.coords.latitude, position.coords.longitude];
        setMapCenter(nextCenter);
        if (!selectedPosition) {
          onLocationChange(position.coords.latitude.toString(), position.coords.longitude.toString());
        }
      },
      () => {
        setMapCenter(FALLBACK_CENTER);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  }, [enabled, onLocationChange, selectedPosition]);

  useEffect(() => {
    if (selectedPosition) {
      setMapCenter(selectedPosition);
    }
  }, [selectedPosition]);

  if (!enabled) return null;

  const markerPosition = selectedPosition || mapCenter;

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLat = position.coords.latitude;
        const nextLng = position.coords.longitude;
        setMapCenter([nextLat, nextLng]);
        onLocationChange(nextLat.toString(), nextLng.toString());
      },
      () => {
        setMapCenter(FALLBACK_CENTER);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  return (
    <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3 animate-in fade-in slide-in-from-top-2 duration-150">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</p>
          <p className="text-[11px] text-slate-500 mt-1">{note}</p>
        </div>
        <button
          type="button"
          onClick={handleUseCurrentLocation}
          className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-50 transition-colors"
        >
          <LocateFixed className="w-4 h-4" />
          Use current location
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <MapContainer
          center={markerPosition}
          zoom={15}
          scrollWheelZoom
          className="h-[320px] w-full"
        >
          <MapController center={markerPosition} />
          <MapInteractions onSelect={(coords) => {
            onLocationChange(coords[0].toString(), coords[1].toString());
            setMapCenter(coords);
          }} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={markerPosition}
            draggable
            eventHandlers={{
              dragend: (event) => {
                const nextLatLng = event.target.getLatLng();
                const coords = [nextLatLng.lat, nextLatLng.lng];
                onLocationChange(coords[0].toString(), coords[1].toString());
                setMapCenter(coords);
              }
            }}
          />
          <Circle
            center={markerPosition}
            radius={Math.max(Number(radius) || 0, 1)}
            pathOptions={{ color: '#4f46e5', fillColor: '#818cf8', fillOpacity: 0.2, weight: 2 }}
          />
        </MapContainer>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <MapPin className="w-4 h-4 text-indigo-600" />
          <span className="font-semibold">Selected point</span>
          <span className="font-mono text-slate-500">{selectedPosition ? `${selectedPosition[0].toFixed(6)}, ${selectedPosition[1].toFixed(6)}` : 'Pick a point on the map'}</span>
        </div>
        <div className="space-y-1 min-w-[220px] w-full sm:w-auto">
          <label className="block text-[10px] font-bold text-slate-400 uppercase">Radius (Meters)</label>
          <input
            type="range"
            min="50"
            max="5000"
            step="10"
            value={radius}
            onChange={(e) => onRadiusChange(parseInt(e.target.value, 10) || 0)}
            className="w-full accent-indigo-600"
          />
          <div className="flex items-center justify-between text-[11px] text-slate-500 font-medium">
            <span>50m</span>
            <span className="font-bold text-slate-700">{radius || 0}m</span>
            <span>5000m</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeofenceMapPicker;